/**
 * plan-gate — app-wide signal for the billing 402s (audit H8 / L20).
 *
 * The server answers 402 with one of these codes when the plan stops a
 * request: the workspace is read-only (WORKSPACE_LOCKED), the plan lacks a
 * feature or hit a limit, or every seat is taken. `apiFetch` reports each one
 * here; the authenticated shell registers the handler that turns it into a
 * toast with a "View plans" / "Buy seats" action. Public pages register
 * nothing, so a 402 there stays a plain error.
 *
 * A screen that already explains a 402 inline passes the codes it handles in
 * `apiFetch(..., { handledPlanGateCodes })` so the user isn't told twice.
 *
 * G-38c — the other half of "told twice": most handlers also fire their own
 * generic error toast ("Action failed", "Could not resume: …") from a catch.
 * When the shell handler says it SURFACED the 402 (returns true), two things
 * record that: the thrown ApiError carries `planGateReported` (a precise check
 * for call sites — `isPlanGateReported`), and `planGateJustSurfaced()` stays
 * true for PLAN_GATE_ECHO_MS, which the toast provider uses to drop the
 * handler's echo error toast centrally. Nothing surfaced (public page, billing
 * page, super admin) → nothing is marked, so the handler's own toast still shows.
 *
 * No imports — api-client depends on this module, never the other way round.
 */

export const PLAN_GATE_CODES = ['WORKSPACE_LOCKED', 'PLAN_FEATURE_LOCKED', 'PLAN_LIMIT_REACHED', 'SEAT_LIMIT'] as const
export type PlanGateCode = (typeof PLAN_GATE_CODES)[number]

export type PlanGateEvent = { code: PlanGateCode; message: string; details?: unknown }

export function isPlanGateCode(code: string): code is PlanGateCode {
  return (PLAN_GATE_CODES as ReadonlyArray<string>).includes(code)
}

/** Return `true` when the 402 was surfaced to the user (toasted, or a toast for it is still up). */
export type PlanGateHandler = (event: PlanGateEvent) => unknown

let handler: PlanGateHandler | null = null

/**
 * How long after a surfaced 402 an error toast counts as that 402's echo. The
 * handler's catch runs a few microtasks after apiFetch throws, so this is
 * generous; it is short so an unrelated later failure still gets its toast.
 */
export const PLAN_GATE_ECHO_MS = 1_500

let lastSurfacedAt = Number.NEGATIVE_INFINITY

/** The shell registers one handler; `null` unregisters (on unmount). */
export function setPlanGateHandler(fn: PlanGateHandler | null): void {
  handler = fn
}

/** Hand a 402 to the shell; true when the user was shown it. */
export function reportPlanGate(event: PlanGateEvent): boolean {
  try {
    const surfaced = handler?.(event) === true
    if (surfaced) lastSurfacedAt = Date.now()
    return surfaced
  } catch {
    /* a toast must never break the response path */
    return false
  }
}

/** True for PLAN_GATE_ECHO_MS after the shell surfaced a 402 (see the header). */
export function planGateJustSurfaced(now: number = Date.now()): boolean {
  return now - lastSurfacedAt < PLAN_GATE_ECHO_MS
}

/** Test-only. */
export function _resetPlanGateEcho(): void {
  lastSurfacedAt = Number.NEGATIVE_INFINITY
}

export type PlanGateToast = {
  message: string
  /** null = the viewer can't open billing, so no action is offered. */
  action: { label: string; hash: '' | '#seats' } | null
}

/**
 * Toast copy for a 402. `canOpenBilling` = the viewer has billing.read; without
 * it a "View plans" button would land on a 403, so they're told to ask an admin.
 */
export function planGateToast(event: PlanGateEvent, canOpenBilling: boolean): PlanGateToast {
  const askAdmin = ' Ask a workspace admin to sort out the plan.'
  switch (event.code) {
    case 'WORKSPACE_LOCKED':
      return {
        message: canOpenBilling
          ? 'Workspace is read-only — renew to keep working.'
          : `Workspace is read-only.${askAdmin}`,
        action: canOpenBilling ? { label: 'View plans', hash: '' } : null,
      }
    case 'SEAT_LIMIT':
      return {
        message: canOpenBilling ? event.message || 'Every seat is in use.' : `Every seat is in use.${askAdmin}`,
        action: canOpenBilling ? { label: 'Buy seats', hash: '#seats' } : null,
      }
    case 'PLAN_FEATURE_LOCKED':
    case 'PLAN_LIMIT_REACHED':
      return {
        message: canOpenBilling
          ? event.message || 'Your plan does not include this.'
          : `${event.message || 'Your plan does not include this.'}${askAdmin}`,
        action: canOpenBilling ? { label: 'View plans', hash: '' } : null,
      }
  }
}
